//React and Redux
import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { store } from "../../../../../store";

//Components
import ContinueButton from "./ContinueButton";

//Stripe functions
import { loadStripe } from '@stripe/stripe-js';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';

//Redux actions
import {
    updatePaymentMethodId,
    createStripeCustomer,
    createPaymentIntent,
    updateClientSecret
} from "../../../../../Features/Stripe/StripeSlice";

import {
    createUser,
    grabUserID,
    purgeID,
    activateSubscription,
    checkCouponValidity, checkCouponProducts, grabToken, logIn
} from "../../../../../Features/Auth/authSlice";

import { sendCombined } from "../../../../../Features/Payment/paymentSlice";

//Stripe initialization
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentFormOuter = () => {
    return (
        <Elements stripe={stripePromise}>
            <PaymentFormInner />
        </Elements>
    );
};

const PaymentFormInner = () => {
    //Hooks
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [processed, setProcessed] = useState(false);
    const [postalCode, setPostalCode] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const dispatch = useDispatch();

    //Redux state selectors
    const { paymentMethodId, stripeCustomerId, clientSecret, paymentId } = useSelector(state => state.stripe);
    const {
        first_name,
        last_name,
        email,
        planType,
        kipMateCode,
        password,
        emailVerified,
        id,
        userType
    } = useSelector(state => state.auth)

    const loginData ={
        username: email,
        password
    }

    //constants
    const variation_id = planType === 1 ? 118 : planType === 2 ? 119 : planType === 3 ? 120 : null;
    const amount = planType === 1 ? "1500" : planType === 2 ? "3000" : planType === 3 ? "6000" : null;
    const name = first_name + " " + last_name;
    const userData = {
        first_name,
        last_name,
        email,
        password,
        userType,
        ...(userType === "individual" ? {} : { kipMateCode }),
    };
    const grabData = {
        email,
        password
    };


    //data for payment and coupons
    const data = userType === "corporate" ? {
        customer_id: id,
        email,

        line_items: [
            {
                product_id: 117,
                variation_id,
                quantity: 1,
            },
        ],
        billing: {
            first_name: first_name,
            last_name: last_name,
            postcode: postalCode,
            country: "GB",
            email: email,
        },
        coupon_lines: [
            {
                code: kipMateCode,
                discount: "1"
            },
        ],
    } : {
        payment_method: "stripe",
        payment_method_title: "Credit Card (Stripe)",
        customer_id: id,
        set_paid: true,
        email,

        line_items: [
            {
                product_id: 117,
                variation_id,
                quantity: 1,
            },
        ],
        billing: {
            first_name: first_name,
            last_name: last_name,
            postcode: postalCode,
            country: "GB",
            email: email,
        },
        meta_data: [
            {
                key: "_stripe_customer_id",
                value: stripeCustomerId
            },
            {
                key: "_stripe_payment_method_id",
                value: paymentMethodId
            },
        ],
    };

    //Event handlers
    const handlePostalCodeChange = (event) => {
        setPostalCode(event.target.value);
    };

    const handleCorporateSubmit = async (event) => {
        dispatch(checkCouponProducts(kipMateCode))
        event.preventDefault();
        setProcessing(true);

        try {
            await submitPaymentMethodToServer(userType, kipMateCode)
        } catch (error) {
            console.error('Error in Promise chain:', error);
            setProcessing(false);
        }
    };

    const handleIndividualSubmit = async (event) => {
        event.preventDefault();
        setProcessing(true);

        if (!stripe || !elements) {
            setProcessing(false);
            return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);

        const billingDetails = {
            name,
            email,
            address: {
                postal_code: postalCode,
                country: 'UK',
            },
        };

        const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardNumberElement,
            billing_details: billingDetails,
        });

        if (error) {
            console.error('Error creating payment method:', error);
            setProcessing(false);
            return;
        }

        const newPaymentMethodId = paymentMethod.id;

        dispatch(updatePaymentMethodId(newPaymentMethodId));

        dispatch(createStripeCustomer({ email, paymentMethodId: newPaymentMethodId, name }))
            .then((stripeCustomerResponse) => {
                const newStripeCustomerId = stripeCustomerResponse.payload.customerId;


                return dispatch(
                    createPaymentIntent({ paymentMethodId: newPaymentMethodId, stripeCustomerId: newStripeCustomerId, amount }),
                );
            })

            .then(async (stripeCustomerResponse) => {
                const newStripeCustomerId = stripeCustomerResponse.meta.arg.stripeCustomerId
                await submitPaymentMethodToServer(userType, kipMateCode, newStripeCustomerId );
                setProcessed(true);
            })
            .catch((error) => {
                console.error('Error in Promise chain:', error);
                setProcessing(false);
            });
    };


    const submitPaymentMethodToServer = async (userType, kipMateCode, newStripeCustomerId, paymentId) => {
        try{
            if (emailVerified === true) {


                // Dispatch checkCouponValidity if userType is "corporate"
                if (userType === "corporate") {
                    await dispatch(checkCouponValidity(kipMateCode));
                }

                await dispatch(purgeID());

                //create woocommerce user

                await dispatch(createUser(userData));

                //grab the newly created woocommerce id, store it in state
                await dispatch(grabUserID(grabData));

                //grab it from state and save it in a couple of variables
                const state = store.getState();
                const userId = state.auth.id;
                // console.log("userId: ", userId)
                const id = state.auth.id;
                // console.log("id: ", id)

                //add-edit it into the data object, save amalgamation as combinedData
                const combinedData = {...data, customer_id: userId, stripe_customer_id: newStripeCustomerId};
                // console.log("combinedData:", combinedData)

                // send the combined data
                await dispatch(sendCombined(combinedData));

                //activate the subscription once it's created - not automatic on woocommerce
                await dispatch(activateSubscription(id));

                setProcessing(false);
                setProcessed(true);

                //NEW
                await dispatch(grabToken(loginData))
                await dispatch(logIn(loginData))


            } else { alert("Please verify your email"); }

        } catch (error) {
            console.error(error);
        }
    };


    return (
        <>
            <form
                className={`text-xl text-white flex flex-col gap-2`}
                onSubmit={
                    userType === 'corporate'
                        ? handleCorporateSubmit
                        : handleIndividualSubmit
                }
            >
                {userType !== 'corporate' && (
                    <>
                        <label className={`text-sm`} htmlFor="card_number">
                            Card Number
                        </label>
                        <CardNumberElement
                            className={`border-light-border rounded-lg border-2 p-3 text-lg text-gray-400  placeholder-center focus:outline-none focus:border-pink- focus:opacity-100 ${
                                processing ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        />

                        <label className={`text-sm`} htmlFor="card_expiry">
                            Expiry Date
                        </label>
                        <CardExpiryElement
                            className={`border-light-border focus:outline-none focus:border-pink-icon focus:opacity-100 rounded-lg border-2 p-3 text-lg text-gray-800  placeholder-center ${
                                processing ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        />

                        <label className={`text-sm`} htmlFor="card_cvc">
                            CVC
                        </label>
                        <CardCvcElement
                            className={`bg-transparent border-light-border rounded-lg border-2 p-3 text-lg focus:outline-none ${
                                processing ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        />
                    </>
                )}
                <label className={`text-sm mb-1`} htmlFor="postal_code">
                    Postal Code
                </label>
                <input
                    className={`text-sm bg-transparent border-light-border rounded-lg border-2 p-3 text-white focus:outline-none   focus:opacity-100 placeholder-center mb-4 ${
                        processing ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    type="text"
                    id="postal_code"
                    value={postalCode}
                    onChange={handlePostalCodeChange}
                    required
                />

                <div className="w-full flex items-center justify-center pt-2">
                    {processed ? (
                        <ContinueButton />
                    ) : (
                        <button
                            key={processing}
                            className={`bg-gold-accent text-base lg:text-xl hover:bg-gold-accent-hover h-14 px-1 lg:px-4 py-2 rounded-lg w-full justify-self-center cursor-pointer`}
                            disabled={!stripe || processing}
                            type="submit"
                        >
                            {processing ? 'Processing...' : 'Submit'}
                        </button>
                    )}
                </div>
            </form>

        </>
    );

};

export default PaymentFormOuter;